import { BigNumber } from '@ethersproject/bignumber'
import PositionRangeStatus from 'components/Badge/PositionRangeStatus'
import DoubleCurrencyLogo from 'components/DoubleLogo'
import Loader from 'components/Icons/LoadingSpinner'
import {
  CellPrimary,
  CellSecondary,
  formatPositionUSD,
  PoolIdentity,
  PositionCell,
  PositionMetrics,
  PositionTableRow,
  RangeValues,
  TokenDistribution,
  VersionBadge,
} from 'components/PositionList/PositionTable'
import { Position } from 'elephantswapv3-sdk'
import { Percent, Price, Token } from 'elephantswapv3-sdk-core'
import { useToken } from 'hooks/Tokens'
import { useColor } from 'hooks/useColor'
import useIsTickAtLimit from 'hooks/useIsTickAtLimit'
import { usePool } from 'hooks/usePools'
import { useUSDPrice } from 'hooks/useUSDPrice'
import { useV3PositionFees } from 'hooks/useV3PositionFees'
import { useEffect, useMemo } from 'react'
import { Bound } from 'state/mint/v3/actions'
import styled from 'styled-components/macro'
import { formatTickPrice } from 'utils/formatTickPrice'
import { unwrappedToken } from 'utils/unwrappedToken'

import { DAI, USDC_MAINNET, USDT, WBTC, WRAPPED_NATIVE_CURRENCY } from '../../constants/tokens'

const LinkRow = styled(PositionTableRow)``

interface PositionListItemProps {
  token0: string
  token1: string
  tokenId: BigNumber
  fee: number
  liquidity: BigNumber
  tickLower: number
  tickUpper: number
  searchQuery?: string
  createdAt?: number
  order?: number
  visible?: boolean
  rangeFilter?: 'all' | 'in-range' | 'out-of-range'
  onPositionMetricsChange?: (positionKey: string, metrics?: PositionMetrics) => void
}

function getPriceOrderingFromPositionForUI(position?: Position): {
  priceLower?: Price<Token, Token>
  priceUpper?: Price<Token, Token>
  quote?: Token
  base?: Token
} {
  if (!position) {
    return {}
  }

  const token0 = position.amount0.currency
  const token1 = position.amount1.currency

  // if token0 is a dollar-stable asset, set it as the quote token
  const stables = [DAI, USDC_MAINNET, USDT]
  if (stables.some((stable) => stable.equals(token0))) {
    return {
      priceLower: position.token0PriceUpper.invert(),
      priceUpper: position.token0PriceLower.invert(),
      quote: token0,
      base: token1,
    }
  }

  // if token1 is an ETH-/BTC-stable asset, set it as the base token
  const bases = [...Object.values(WRAPPED_NATIVE_CURRENCY), WBTC]
  if (bases.some((base) => base && base.equals(token1))) {
    return {
      priceLower: position.token0PriceUpper.invert(),
      priceUpper: position.token0PriceLower.invert(),
      quote: token0,
      base: token1,
    }
  }

  // if both prices are below 1, invert
  if (position.token0PriceUpper.lessThan(1)) {
    return {
      priceLower: position.token0PriceUpper.invert(),
      priceUpper: position.token0PriceLower.invert(),
      quote: token0,
      base: token1,
    }
  }

  // otherwise, just return the default
  return {
    priceLower: position.token0PriceLower,
    priceUpper: position.token0PriceUpper,
    quote: token1,
    base: token0,
  }
}

export default function PositionListItem({
  token0: token0Address,
  token1: token1Address,
  tokenId,
  fee: feeAmount,
  liquidity,
  tickLower,
  tickUpper,
  searchQuery = '',
  createdAt,
  order,
  visible = true,
  rangeFilter = 'all',
  onPositionMetricsChange,
}: PositionListItemProps) {
  const token0 = useToken(token0Address)
  const token1 = useToken(token1Address)

  const currency0 = token0 ? unwrappedToken(token0) : undefined
  const currency1 = token1 ? unwrappedToken(token1) : undefined

  // construct Position from details returned
  const [, pool] = usePool(currency0 ?? undefined, currency1 ?? undefined, feeAmount)

  const position = useMemo(() => {
    if (pool) {
      return new Position({ pool, liquidity: liquidity.toString(), tickLower, tickUpper })
    }
    return undefined
  }, [liquidity, pool, tickLower, tickUpper])

  const tickAtLimit = useIsTickAtLimit(feeAmount, tickLower, tickUpper)

  // prices
  const { priceLower, priceUpper, quote, base } = getPriceOrderingFromPositionForUI(position)

  const currencyQuote = quote && unwrappedToken(quote)
  const currencyBase = base && unwrappedToken(base)

  // check if price is within range
  const outOfRange: boolean = pool ? pool.tickCurrent < tickLower || pool.tickCurrent >= tickUpper : false

  const positionSummaryLink = '/pools/' + tokenId

  const removed = liquidity?.eq(0)

  const { data: token0USDValue } = useUSDPrice(position?.amount0)
  const { data: token1USDValue } = useUSDPrice(position?.amount1)
  const [feeValue0, feeValue1] = useV3PositionFees(pool ?? undefined, tokenId)
  const { data: feeValue0USD } = useUSDPrice(feeValue0)
  const { data: feeValue1USD } = useUSDPrice(feeValue1)

  const estimatedValue = useMemo(() => {
    const value =
      token0USDValue !== undefined && token1USDValue !== undefined
        ? token0USDValue + token1USDValue
        : token0USDValue !== undefined
        ? token0USDValue * 2
        : token1USDValue !== undefined
        ? token1USDValue * 2
        : undefined
    return value !== undefined && Number.isFinite(value) ? value : undefined
  }, [token0USDValue, token1USDValue])

  const feesUSD = useMemo(() => {
    if (!feeValue0 || !feeValue1) return undefined
    const availableValues = [feeValue0USD, feeValue1USD].filter((value): value is number => value !== undefined)
    if (!availableValues.length) return undefined
    return availableValues.reduce((total, value) => total + value, 0)
  }, [feeValue0, feeValue0USD, feeValue1, feeValue1USD])

  const token0Percent = useMemo(() => {
    if (token0USDValue !== undefined && token1USDValue !== undefined && token0USDValue + token1USDValue > 0) {
      return (token0USDValue / (token0USDValue + token1USDValue)) * 100
    }
    if (position?.amount0.equalTo(0)) return 0
    if (position?.amount1.equalTo(0)) return 100
    return 50
  }, [position, token0USDValue, token1USDValue])

  const oneLocked = useMemo(() => {
    if (!position) return undefined
    const wrappedOne = WRAPPED_NATIVE_CURRENCY[position.pool.chainId]
    if (!wrappedOne) return 0
    const amount = position.pool.token0.equals(wrappedOne)
      ? position.amount0
      : position.pool.token1.equals(wrappedOne)
      ? position.amount1
      : undefined
    if (!amount) return 0
    const numericAmount = Number(amount.toSignificant(12))
    return Number.isFinite(numericAmount) ? numericAmount : undefined
  }, [position])

  useEffect(() => {
    const positionKey = `v3:${tokenId.toString()}`
    onPositionMetricsChange?.(positionKey, { estimatedValue, feesUSD, createdAt, oneLocked })
    return () => onPositionMetricsChange?.(positionKey, undefined)
  }, [createdAt, estimatedValue, feesUSD, onPositionMetricsChange, oneLocked, tokenId])

  const token0Color = useColor(token0 ?? undefined)
  const token1Color = useColor(token1 ?? undefined, '#9B5DE5')

  const normalizedSearch = searchQuery.trim().toLowerCase()
  const matchesSearch = [token0Address, token1Address, token0?.symbol, token1?.symbol, tokenId.toString()]
    .filter(Boolean)
    .some((value) => value?.toLowerCase().includes(normalizedSearch))

  const matchesRange = rangeFilter === 'all' || (rangeFilter === 'in-range' ? !outOfRange : outOfRange)
  const hidden = !visible || !matchesRange || Boolean(normalizedSearch && !matchesSearch)

  const lowerPrice =
    priceLower &&
    formatTickPrice({
      price: priceLower,
      atLimit: tickAtLimit,
      direction: Bound.LOWER,
    })
  const upperPrice =
    priceUpper &&
    formatTickPrice({
      price: priceUpper,
      atLimit: tickAtLimit,
      direction: Bound.UPPER,
    })

  return (
    <LinkRow to={positionSummaryLink} $order={order} $hidden={hidden}>
      <PositionCell>
        <PoolIdentity>
          <DoubleCurrencyLogo currency0={currency0} currency1={currency1} size={20} margin />
          <div>
            <CellPrimary>
              {currency0?.symbol} / {currency1?.symbol}
            </CellPrimary>
            <CellSecondary>
              <VersionBadge>v3</VersionBadge>
              <span>{new Percent(feeAmount, 1_000_000).toSignificant()}%</span>
            </CellSecondary>
          </div>
        </PoolIdentity>
      </PositionCell>
      <PositionCell>
        {lowerPrice && upperPrice ? (
          <>
            <RangeValues>
              <span>{lowerPrice}</span>
              <span>→</span>
              <span>{upperPrice}</span>
            </RangeValues>
            <CellSecondary>
              {currencyQuote?.symbol} per {currencyBase?.symbol}
            </CellSecondary>
            <CellSecondary>
              <PositionRangeStatus removed={removed} inRange={!outOfRange} />
            </CellSecondary>
          </>
        ) : (
          <Loader />
        )}
      </PositionCell>
      <PositionCell>
        <TokenDistribution
          firstPercent={token0Percent}
          firstColor={token0Color}
          secondColor={token1Color}
          firstSymbol={currency0?.symbol}
          secondSymbol={currency1?.symbol}
        />
      </PositionCell>
      <PositionCell>
        <CellPrimary>{formatPositionUSD(estimatedValue)}</CellPrimary>
      </PositionCell>
      <PositionCell>
        <CellPrimary>{formatPositionUSD(feesUSD)}</CellPrimary>
      </PositionCell>
      <PositionCell>
        <CellPrimary>
          {createdAt
            ? new Intl.DateTimeFormat(undefined, { year: 'numeric', month: 'short', day: 'numeric' }).format(
                new Date(createdAt * 1000)
              )
            : '—'}
        </CellPrimary>
      </PositionCell>
    </LinkRow>
  )
}
